import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import { isNativeApp, isBusinessApp } from '../../utils/platform';
import { isMobileWeb, isAndroidWeb, isIOSSafari } from '../../utils/mobileWeb';

// "Petzy is better in the app" bar, for the visitors Apple's Smart App Banner
// can't reach.
//
// That banner (the apple-itunes-app meta in index.html) is free and better than
// anything we'd build — Apple styles it, keeps it current, and says "Open"
// rather than "Get" when the app is already installed, which a web page cannot
// detect. It only renders in Safari on iOS. Not Chrome for iPhone, not Android
// at all, and — the gap that matters — not inside the in-app browsers of
// Instagram, Facebook, TikTok or WhatsApp, which is where most social traffic
// lands. This fills exactly that hole and defers everywhere else.
//
// WHERE IT STAYS QUIET:
//   • iOS Safari — Apple's own banner is already there; two stacked is worse
//     than one good one.
//   • desktop — you can't install a phone app on a laptop.
//   • our own native apps — obviously.
//   • the Business build — its visitors are salon owners; the consumer app is
//     not what they came for.
//   • mid-flow routes — someone booking or signing in is converting. Interrupting
//     that to advertise costs more than the install is worth.
//
// THE ONE GAP WE ACCEPTED (decided 2026-07-16, don't re-litigate silently):
// Apple's tag applies to EVERY route, /pro included, so a salon owner reading the
// B2B pitch in Safari is offered the CONSUMER app. Safari reads the meta once at
// page load and this SPA ships one index.html for all routes — there is no
// per-route variant, and swapping the tag from JS afterwards does not redraw the
// banner. Two fixes were weighed and declined:
//   • give /pro its own HTML entry (as terms.html already has). Correct, but a
//     separate bundle for one marketing page.
//   • drop Apple's tag and use only this component everywhere. Full per-route
//     control, but it loses what Apple's is actually good at — the App Store
//     knows whether the app is installed and offers "Open" instead of "Install",
//     which no web page can detect.
// The fix shipped 2026-07-24, once Petzy Business went live on BOTH stores: the
// /pro marketing pages now get a BUSINESS variant of this bar (own copy, own
// store links, own dismissal key) instead of being skipped. What remains of the
// gap is only iOS Safari on /pro, where Apple's consumer banner still renders
// and stacking ours under it would be worse — wrong audience, not a false
// claim, and /pro's Safari traffic is thin. Do NOT instead point the index.html
// tag at Business — that breaks every other page, where the visitors are.

// Two variants of the same bar: the consumer app everywhere, the Business app
// on the /pro marketing pages — each with its own dismissal key, so a salon
// owner who closed the consumer bar weeks ago still sees the Business one.
// Copy names the one capability the web build genuinely lacks (push): for
// clients that's the appointment reminder, for owners the new-booking alert.
const VARIANTS = {
  client: {
    storageKey: 'petzy_app_banner_dismissed',
    appStoreUrl: 'https://apps.apple.com/ro/app/id6788333275',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.mariusnpop.petzy',
    message: 'Notificări pentru programările tale',
  },
  business: {
    storageKey: 'petzy_app_banner_dismissed_pro',
    appStoreUrl: 'https://apps.apple.com/ro/app/id6790619227',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.mariusnpop.petzy.business',
    message: 'Notificări la fiecare programare nouă',
  },
};

// Don't interrupt a conversion or an auth step. Booking and checkout are the
// whole point of the site; /pro/login and /pro/register are the B2B funnel's
// own auth steps (bare '/login' and '/register' don't prefix-match them). The
// /pro marketing pages themselves carry the Business variant, so they are no
// longer skipped wholesale. Side effect, intended: the old bare '/pro' entry
// also prefix-matched '/profile', which therefore never showed the bar; it's a
// content page like any other, so it now gets the consumer variant.
const SKIP_PREFIXES = [
  '/booking', '/checkout', '/login', '/register', '/pro/login', '/pro/register',
  '/forgot-password', '/reset-password', '/verify-email', '/accept-invite',
];

// localStorage throws outright in some private/sandboxed contexts, so every
// access is guarded — the same rule utils/chunkReload follows. A storage failure
// must degrade to "show the banner", never to a crashed render.
function wasDismissed(storageKey) {
  try {
    return localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

function rememberDismissal(storageKey) {
  try {
    localStorage.setItem(storageKey, '1');
  } catch {
    /* storage off — the banner returns next load, which beats a thrown error */
  }
}

// `className` lets a caller position the bar for its own layout: AppShell
// renders it bare, first in the flow above AppHeader; ProNavBar passes mt-16
// so the bar clears its fixed h-16 nav — applied HERE so the offset exists
// only when the bar actually renders, never as an empty spacer.
export default function AppPromoBanner({ className = '' }) {
  const { pathname } = useLocation();
  // State only forces the re-render on dismiss and keeps the bar hidden for
  // the session when localStorage is unavailable. The durable answer is
  // re-read from localStorage each render, because this component survives
  // route changes and the /pro variant uses a different key.
  const [sessionDismissed, setSessionDismissed] = useState(() => new Set());

  const isProPage = pathname === '/pro' || pathname.startsWith('/pro/');
  const variant = isProPage ? VARIANTS.business : VARIANTS.client;

  const eligible = isMobileWeb && !isNativeApp && !isBusinessApp && !isIOSSafari;
  const onSkippedPath = SKIP_PREFIXES.some((p) => pathname.startsWith(p));
  const dismissed = sessionDismissed.has(variant.storageKey) || wasDismissed(variant.storageKey);
  if (!eligible || onSkippedPath || dismissed) return null;

  const href = isAndroidWeb ? variant.playStoreUrl : variant.appStoreUrl;

  const handleDismiss = () => {
    rememberDismissal(variant.storageKey);
    setSessionDismissed((prev) => new Set(prev).add(variant.storageKey));
  };

  return (
    <div className={`bg-ink text-cream-50 ${className}`}>
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5">
        <img src="/apple-touch-icon.png" alt="" aria-hidden="true" className="h-9 w-9 shrink-0 rounded-lg" />
        {/* Names the one thing the app actually does that this page cannot,
            rather than claiming it's "better". The app is the same web bundle in
            a WebView — but the web build has no service worker, no web-push, no
            PushManager, so a browser genuinely cannot remind anyone about their
            appointment. @capacitor/push-notifications can, and does.
            No `truncate`: at 375px the icon, CTA and close button leave ~180px,
            and truncating severed the line mid-word. Two wrapped lines read fine. */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug">{variant.message}</p>
        </div>
        {/* "Instalează", not "Deschide". Apple's banner can say Open because it
            knows whether the app is on the device; a web page cannot. This links
            to a store listing, so for the people it's aimed at — the ones without
            the app — "Deschide" would simply be a lie. */}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded-full bg-primary-500 px-4 py-1.5 text-sm font-bold text-white transition hover:bg-primary-600"
        >
          Instalează
        </a>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Închide"
          className="-mr-1 shrink-0 rounded-full p-1.5 text-cream-50/50 transition hover:bg-cream-50/10 hover:text-cream-50"
        >
          <FiX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
